import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../../data";

const LINKS = [
  { label: "Work", href: "#gallery" },
  { label: "Packages", href: "#packages" },
  { label: "Story", href: "#story" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

export default function Nav({ onBook }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2"
        >
          <img src="/brand/logo.jpeg" alt="Pine & Shine" className="h-9 w-9 rounded-full object-cover" />
          <span className="font-display text-lg leading-none">Pine &amp; Shine</span>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-sm font-semibold uppercase tracking-widest text-ink/70 transition-colors hover:text-ink"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-call"
            href={BUSINESS.phoneHref}
            className="hidden items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors hover:bg-ink hover:text-cream sm:flex"
          >
            <Phone size={14} /> Call
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="hidden rounded-full bg-ink px-5 py-2 text-xs font-bold uppercase tracking-widest text-cream transition-transform active:scale-95 sm:block"
          >
            Book Now
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-ink/20 p-2 md:hidden"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-ink/10 bg-cream md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="border-b border-ink/10 py-3 text-left font-display text-2xl"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-btn"
                onClick={() => { setOpen(false); onBook(); }}
                className="mt-4 rounded-full bg-ink py-4 text-sm font-bold uppercase tracking-widest text-cream"
              >
                Book an Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
